import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import OutlinedButton from "../../../../components/buttons/OutlinedButton";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons } from "../../../stack/environment/icons/iconExport";
import {
  getActionById,
  getConditionById,
  getConditionTypeById,
  getTriggerById,
} from "../data/workflowOptions";
import SequentialWorkflowBuilder from "./SequentialWorkflowBuilder";

const BuildWorkflowModal = ({
  open,
  onClose,
  onWorkflowCreated,
  editingWorkflow,
}) => {
  const colors = useColors();
  const [workflowName, setWorkflowName] = useState("");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const { user } = useAuth0();

  useEffect(() => {
    if (editingWorkflow && open) {
      setWorkflowName(editingWorkflow.title || "");
      setWorkflowDescription(editingWorkflow.description || "");

      const trigger = editingWorkflow.triggers?.[0];
      if (trigger) {
        const fullTrigger = getTriggerById(trigger.id) || trigger;
        setSelectedTrigger(fullTrigger);
      } else {
        setSelectedTrigger(null);
      }

      setSelectedCondition(null);

      const normalizedConditions = (editingWorkflow.conditions || []).map(
        (condition) => {
          if (condition.type && typeof condition.type === "object") {
            return condition;
          }

          const conditionTypeIds = [
            "severity_is",
            "tool_is",
            "asset_type_is",
            "has_assignee",
            "duration_is",
          ];
          let typeId = null;
          let optionIdStart = 0;

          for (const possibleTypeId of conditionTypeIds) {
            const prefix = possibleTypeId + "_";
            if (condition.id.startsWith(prefix)) {
              typeId = possibleTypeId;
              optionIdStart = prefix.length;
              break;
            }
          }

          if (!typeId) {
            return condition;
          }

          const optionId = condition.id.substring(optionIdStart);
          const conditionType = getConditionTypeById(typeId);
          const conditionOption = getConditionById(typeId, optionId);

          if (conditionType && conditionOption) {
            return {
              ...conditionOption,
              typeChildren: conditionOption.type,
              type: conditionType,
              fullName: `${conditionType.name} ${conditionOption.name}`,
              id: `${conditionType.id}_${conditionOption.id}`,
            };
          }

          return condition;
        }
      );
      setSelectedConditions(normalizedConditions);

      const normalizedActions = (editingWorkflow.actions || []).map(
        (action) => {
          const fullAction = getActionById(action.id) || action;
          return {
            ...fullAction,
            config: fullAction.config || {}, // Keep the schema config
            configValues: action.configValues || action.config || {}, // Pass the actual values separately
          };
        }
      );
      setSelectedActions(normalizedActions);
    } else if (!editingWorkflow && open) {
      setWorkflowName("");
      setWorkflowDescription("");
      setSelectedTrigger(null);
      setSelectedCondition(null);
      setSelectedConditions([]);
      setSelectedActions([]);
    }
  }, [editingWorkflow, open]);

  const handleSave = () => {
    // Validation
    if (!workflowName.trim()) {
      Toast.error("Please enter a workflow name");
      return;
    }

    if (!selectedTrigger) {
      Toast.error("Please select a trigger");
      return;
    }

    if (selectedActions.length === 0) {
      Toast.error("Please select at least one action");
      return;
    }

    // Create workflow object
    const workflow = {
      ...(editingWorkflow ? { id: editingWorkflow.id } : {}),
      title: workflowName.trim(),
      description: workflowDescription.trim(),
      triggers: [selectedTrigger],
      conditions: selectedConditions,
      actions: selectedActions,
      ...(editingWorkflow
        ? {}
        : {
            createdAt: new Date().toISOString(),
            createdBy: user.email,
          }),
      inUse: true,
      available: true,
    };

    // Call the callback to add workflow to the system
    if (onWorkflowCreated) {
      onWorkflowCreated(workflow);
    }

    Toast.success(
      editingWorkflow
        ? "Workflow updated successfully!"
        : "Workflow created successfully!"
    );
    handleClose();
  };

  const handleRunTest = () => {
    if (!selectedTrigger) {
      Toast.error("Please select a trigger to test");
      return;
    }
    const TEST_TIMEOUT_MS = 2000;
    Toast.info("Running workflow test...");
    // Simulate test run
    setTimeout(() => {
      Toast.success("Workflow test completed successfully!");
    }, TEST_TIMEOUT_MS);
  };

  const handleClose = () => {
    // Reset form
    setWorkflowName("");
    setWorkflowDescription("");
    setSelectedTrigger(null);
    setSelectedCondition(null);
    setSelectedConditions([]);
    setSelectedActions([]);
    onClose();
  };

  const handleTriggerSelect = (trigger) => {
    setSelectedTrigger(trigger);
    // Reset subsequent selections when trigger changes
    setSelectedCondition(null);
    setSelectedConditions([]);
    setSelectedActions([]);
  };

  const handleConditionSelect = (condition) => {
    setSelectedCondition(condition);
    // Reset actions when condition changes
    setSelectedActions([]);
  };

  const handleConditionAdd = (condition) => {
    setSelectedConditions((prev) => [...prev, condition]);
  };

  const handleConditionRemove = (conditionId) => {
    setSelectedConditions((prev) => prev.filter((c) => c.id !== conditionId));
  };

  const handleActionAdd = (action) => {
    setSelectedActions((prev) => [...prev, action]);
  };

  const handleActionRemove = (actionId) => {
    setSelectedActions((prev) => prev.filter((a) => a.id !== actionId));
  };

  const handleActionsUpdate = (updatedActions) => {
    setSelectedActions(updatedActions);
  };

  const isFormValid =
    workflowName.trim() && selectedTrigger && selectedActions.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          height: "92vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 24px",
          borderBottom: `1px solid ${colors.grey_20percent}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {editingWorkflow ? "Edit workflow" : "Create a new workflow"}
            </Typography>
            <Box
              onClick={handleClose}
              sx={{ color: colors.text_secondary, cursor: "pointer" }}
            >
              <SvgToIcon
                IconLogo={icons.CloseIcon}
                style={{
                  color: colors.action_active,
                  width: "20px",
                  height: "20px",
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: colors.text_secondary, mt: 0.5 }}
          >
            First-match: evaluate rules top →bottom; stop after the first that
            runs.
          </Typography>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ padding: "24px", overflow: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", mt: 2, gap: 3 }}>
          {/* Sequential Workflow Builder */}
          <SequentialWorkflowBuilder
            key={editingWorkflow?.id || "new"}
            workflowName={workflowName}
            workflowDescription={workflowDescription}
            selectedTrigger={selectedTrigger}
            selectedCondition={selectedCondition}
            selectedConditions={selectedConditions}
            selectedActions={selectedActions}
            onTriggerSelect={handleTriggerSelect}
            onConditionSelect={handleConditionSelect}
            onConditionAdd={handleConditionAdd}
            onConditionRemove={handleConditionRemove}
            onActionAdd={handleActionAdd}
            onActionRemove={handleActionRemove}
            onActionsUpdate={handleActionsUpdate}
            setWorkflowName={setWorkflowName}
            setWorkflowDescription={setWorkflowDescription}
          />
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderTop: `1px solid ${colors.grey_20percent}`,
          backgroundColor: colors.grey_2percent,
        }}
      >
        <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>

        <Box sx={{ display: "flex", gap: 2 }}>
          <OutlinedButton onClick={handleRunTest} disabled={!selectedTrigger}>
            Run a Test
          </OutlinedButton>
          <ContainedButton
            onClick={handleSave}
            disabled={!isFormValid}
            color="primary"
            size="small"
          >
            {editingWorkflow ? "Update" : "Create"}
          </ContainedButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BuildWorkflowModal;
